/**
 * LiveUpdateManager - Centralized management for all live updates in the application
 *
 * ENHANCED WITH OPENAPI TYPES
 */

import { ref, computed } from 'vue'
import { useNetworkStore } from '@/stores/network'
import { useStreamingStore } from '@/stores/streaming'
import { useLiveUpdates } from '@/composables/useLiveUpdates'
import { useUpdateAnimations } from '@/composables/useUpdateAnimations'
import type { components } from '@/types/api'

// Use auto-generated types from OpenAPI spec
type MessageSummary = components['schemas']['MessageSummary']
type TransactionEntry = components['schemas']['TransactionEntry']
type SupplyResponse = components['schemas']['SupplyResponse']
type TreasuryResponse = components['schemas']['TreasuryResponse']
type EscrowTransactionEntry = components['schemas']['EscrowTransactionEntry']
type DiversityMetrics = components['schemas']['DiversityMetrics']
type AdmissibilityOverviewData = components['schemas']['AdmissibilityOverviewData']
type ActiveSync = components['schemas']['ActiveSync']
type ProposalSummary = components['schemas']['ProposalSummary']

interface UpdateMetrics {
  totalReceived: number
  totalProcessed: number
  averageLatency: number
  droppedUpdates: number
  lastUpdateTime: Date | null
  updateRate: number // updates per second
}

interface StateVersion {
  version: number
  checksum: string
  timestamp: string
}

export class LiveUpdateManager {
  private static instance: LiveUpdateManager | null = null

  // WebSocket connection
  private socket: WebSocket | null = null
  private connectionStatus = ref<'disconnected' | 'connecting' | 'connected'>('disconnected')
  private reconnectAttempts = 0
  private maxReconnectAttempts = 10
  private reconnectDelay = 1000
  private heartbeatInterval: number | null = null

  // State management
  private stateVersion = ref<StateVersion | null>(null)
  private pendingSyncRequest = false
  private syncInProgress = false

  // Update handling - lazy initialized to avoid Vue/Pinia issues at module load time
  private _liveUpdates?: ReturnType<typeof useLiveUpdates>
  private _animations?: ReturnType<typeof useUpdateAnimations>
  private _streamingStore?: ReturnType<typeof useStreamingStore>

  private get liveUpdates() {
    if (!this._liveUpdates) {
      this._liveUpdates = useLiveUpdates()
    }
    return this._liveUpdates
  }

  private get animations() {
    if (!this._animations) {
      this._animations = useUpdateAnimations()
    }
    return this._animations
  }

  private get streamingStore() {
    if (!this._streamingStore) {
      this._streamingStore = useStreamingStore()
    }
    return this._streamingStore
  }

  // Metrics
  private metrics = ref<UpdateMetrics>({
    totalReceived: 0,
    totalProcessed: 0,
    averageLatency: 0,
    droppedUpdates: 0,
    lastUpdateTime: null,
    updateRate: 0
  })

  private updateTimestamps: number[] = []
  private latencyMeasurements: number[] = []

  // Singleton pattern
  static getInstance(): LiveUpdateManager {
    if (!LiveUpdateManager.instance) {
      LiveUpdateManager.instance = new LiveUpdateManager()
    }
    return LiveUpdateManager.instance
  }

  private constructor() {
    this.setupEventHandlers()
  }

  /**
   * Initialize the live update system
   */
  async initialize() {
    console.log('Initializing LiveUpdateManager')

    // Connect WebSocket
    await this.connect()

    // Setup update subscriptions
    this.setupUpdateSubscriptions()

    // Start metrics collection
    this.startMetricsCollection()
  }

  /**
   * Connect to WebSocket server
   */
  private async connect() {
    if (this.socket?.readyState === WebSocket.OPEN) {
      console.log('✅ [LiveUpdateManager] WebSocket already connected')
      return
    }

    this.connectionStatus.value = 'connecting'
    const wsUrl = import.meta.env.VITE_WS_URL || 'ws://localhost:9122/ws'

    console.log('🔌 [LiveUpdateManager] Connecting to WebSocket:', wsUrl)

    try {
      this.socket = new WebSocket(wsUrl)
      this.setupSocketHandlers()
    } catch (error) {
      console.error('❌ [LiveUpdateManager] Failed to create WebSocket:', error)
      this.handleConnectionError()
    }
  }

  /**
   * Setup WebSocket event handlers
   */
  private setupSocketHandlers() {
    if (!this.socket) return

    this.socket.onopen = () => {
      console.log('✅ [LiveUpdateManager] WebSocket CONNECTED successfully!')
      this.connectionStatus.value = 'connected'
      this.reconnectAttempts = 0
      this.reconnectDelay = 1000

      // Update streaming store status
      this.streamingStore.fetchStatus()

      // Subscribe to all channels
      console.log('📡 [LiveUpdateManager] Subscribing to channels...')
      this.subscribeToChannels()

      // Start heartbeat
      this.startHeartbeat()
    }

    this.socket.onclose = (event) => {
      console.log('🔌 [LiveUpdateManager] WebSocket DISCONNECTED:', {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean
      })
      this.connectionStatus.value = 'disconnected'
      this.stopHeartbeat()

      // Update streaming store status
      this.streamingStore.fetchStatus()

      this.handleDisconnection()
    }

    this.socket.onerror = (error) => {
      console.error('❌ [LiveUpdateManager] WebSocket ERROR:', error)
      this.handleConnectionError()
    }

    this.socket.onmessage = (event) => {
      this.handleMessage(event)
    }
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(event: MessageEvent) {
    try {
      const data = JSON.parse(event.data)
      const receiveTime = Date.now()

      // 🔍 DEBUG: Log all incoming WebSocket messages
      console.log('📨 [LiveUpdateManager] WebSocket message received:', {
        type: data.type,
        hasData: !!data.data,
        fullMessage: data
      })

      // Update metrics
      this.metrics.value.totalReceived++
      this.updateTimestamps.push(receiveTime)

      // Handle initial connection with state
      if (data.type === 'connection' && data.initial_state) {
        console.log('🔗 [LiveUpdateManager] Initial connection with state')
        this.handleInitialState(data)
        return
      }

      // Check if we need state sync
      if (data.state_version && this.stateVersion.value) {
        if (data.state_version > this.stateVersion.value.version + 10) {
          // We're too far behind, request full sync
          this.requestStateSync()
          return
        }
      }

      // Calculate latency if event_timestamp present (original node timestamp)
      // Falls back to processed_at (backend time) if event_timestamp not available
      const timestampField = data.event_timestamp || data.processed_at || data.timestamp
      if (timestampField) {
        // Handle Unix timestamp (seconds) from node
        let sentTime: number
        if (typeof timestampField === 'number') {
          sentTime = timestampField * 1000  // Convert to milliseconds
        } else {
          sentTime = new Date(timestampField).getTime()
        }
        const latency = receiveTime - sentTime
        this.latencyMeasurements.push(latency)
      }

      // Process the update
      console.log('⚙️ [LiveUpdateManager] Processing update:', data.type)
      this.processUpdate(data)

      // Update metrics
      this.metrics.value.totalProcessed++
      this.metrics.value.lastUpdateTime = new Date(receiveTime)

    } catch (error) {
      console.error('❌ [LiveUpdateManager] Failed to handle WebSocket message:', error)
      this.metrics.value.droppedUpdates++
    }
  }

  /**
   * Handle initial state from server
   */
  private handleInitialState(data: {
    state_version?: number
    initial_state?: {
      checksum: string
      timestamp: string
      dag?: {
        total_messages: number
        depth: number
        tips: string[]
      }
      consensus?: {
        diversity_score: number
        active_conflicts: number
        admissibility_rate: number
        security_score: number
      }
      finality?: {
        kcore_finalized: number
        homology_finalized: number
      }
    }
  }) {
    console.log('Received initial state, version:', data.state_version)

    // Update state version
    this.stateVersion.value = {
      version: data.state_version,
      checksum: data.initial_state.checksum,
      timestamp: data.initial_state.timestamp
    }

    // Apply initial state to stores
    const networkStore = useNetworkStore()
    const state = data.initial_state

    // Update DAG state
    if (state.dag) {
      networkStore.totalMessages = state.dag.total_messages
      networkStore.dagDepth = state.dag.depth
      networkStore.tips = state.dag.tips
    }

    // Update consensus state
    if (state.consensus) {
      networkStore.diversityScore = state.consensus.diversity_score
      networkStore.activeConflicts = state.consensus.active_conflicts
      networkStore.admissibilityRate = state.consensus.admissibility_rate
      networkStore.securityScore = state.consensus.security_score
    }

    // Update finality state
    if (state.finality) {
      networkStore.finalizedCount = state.finality.kcore_finalized + state.finality.homology_finalized
    }

    console.log('Initial state applied successfully')
  }

  /**
   * Process an update based on its type
   */
  private processUpdate(data: any) {
    const { type, data: updateData, priority = 'medium' } = data

    console.log('🔀 [LiveUpdateManager] Dispatching event:', { type, priority, updateData })

    // Dispatch to appropriate handler with animation
    switch (type) {
      case 'message.new':
        this.handleNewMessage(updateData, priority)
        break
      case 'finality.kcore':
      case 'finality.homology':
        this.handleFinalityUpdate(type, updateData, priority)
        break
      case 'metrics.update':
        this.handleMetricsUpdate(updateData)
        break
      case 'energy.update':
        this.handleEnergyUpdate(updateData, priority)
        break
      case 'diversity.update':
        this.handleDiversityUpdate(updateData)
        break
      case 'economics.update':
        this.handleEconomicsUpdate(updateData)
        break
      case 'treasury.update':
        this.handleTreasuryUpdate(updateData, priority)
        break
      case 'balance.changed':
        this.handleBalanceChanged(updateData, priority)
        break
      case 'transfer.recorded':
      case 'transaction.new':  // Backwards compatibility
        this.handleTransaction(updateData, priority)
        break
      case 'kcore.update':
        this.handleKCoreUpdate(updateData)
        break
      case 'admissibility.update':
        this.handleAdmissibilityUpdate(updateData)
        break
      case 'message.rejected':
        this.handleMessageRejected(updateData, priority)
        break
      case 'reputation.update':
        this.handleReputationUpdate(updateData)
        break
      case 'tips.update':
        this.handleTipsUpdate(updateData)
        break
      case 'deposit.escrowed':
      case 'deposit.released':
      case 'deposit.slashed':
        this.handleDepositEvent(type, updateData, priority)
        break
      case 'peer.connected':
      case 'peer.disconnected':
      case 'peer.stats':
        this.handlePeerEvent(type, updateData, priority)
        break
      case 'sync.started':
      case 'sync.progress':
      case 'sync.completed':
        this.handleSyncEvent(type, updateData, priority)
        break
      case 'node.started':
      case 'node.stopped':
      case 'node.genesis':
      case 'genesis.loaded':  // Alias for node.genesis
        this.handleNodeEvent(type, updateData, priority)
        break
      case 'heartbeat':
      case 'subscribed':
        // Silently handle protocol messages (no logging needed)
        break
      default:
        // Dispatch as custom event for components to handle
        console.log('🔔 [LiveUpdateManager] Dispatching custom event (not handled by switch):', type)
        window.dispatchEvent(new CustomEvent(type, { detail: updateData }))
    }
  }

  /**
   * Handle new message with animation
   */
  private handleNewMessage(data: Partial<MessageSummary> & { message_id?: string }, priority: string) {
    // Dispatch event
    window.dispatchEvent(new CustomEvent('dag.message.new', { detail: data }))

    // Animate if high priority
    if (priority === 'high' && data.message_id) {
      this.animations.pulseElement(`message-${data.message_id}`, {
        color: '#10b981',
        duration: 600
      })
    }
  }

  /**
   * Handle finality update with animation
   */
  private handleFinalityUpdate(type: string, data: {
    message_id?: string
    k_core_value?: number
    homology_betti_b1?: number
  }, priority: string) {
    window.dispatchEvent(new CustomEvent(type, { detail: data }))

    // Flash finality indicator
    if (data.message_id) {
      this.animations.flashValue(`finality-${data.message_id}`, {
        color: type.includes('kcore') ? '#3b82f6' : '#8b5cf6'
      })
    }
  }

  /**
   * Handle metrics update
   */
  private handleMetricsUpdate(data: {
    tps?: number
    dag_depth?: number
    network_height?: number
    finalized_height?: number
    total_messages?: number
    finalized_count?: number
  }) {
    const networkStore = useNetworkStore()

    // Animate TPS changes
    if (data.tps !== undefined && data.tps !== networkStore.tps) {
      const oldTps = networkStore.tps
      networkStore.tps = data.tps

      // Animate TPS value
      this.animations.animateNumber('tps-value', oldTps, data.tps, {
        duration: 500,
        decimals: 2
      })
    }

    // Update other metrics
    if (data.dag_depth !== undefined) {
      networkStore.dagDepth = data.dag_depth
      this.animations.animateProgress('depth-progress', (data.dag_depth / 12) * 100)
    }

    window.dispatchEvent(new CustomEvent('metrics.update', { detail: data }))
  }

  /**
   * Handle energy update
   */
  private handleEnergyUpdate(data: {
    active_conflicts?: number
    new_conflict?: boolean
    [key: string]: any
  }, priority: string) {
    window.dispatchEvent(new CustomEvent('energy.update', { detail: data }))

    // Alert animation for new conflicts
    if (data.new_conflict && priority === 'high') {
      this.animations.shake('conflict-indicator')
      this.animations.highlightBorder('energy-panel', {
        color: '#ef4444',
        duration: 2000
      })
    }
  }

  /**
   * Handle diversity update
   */
  private handleDiversityUpdate(data: Partial<DiversityMetrics> & { diversity_score?: number }) {
    window.dispatchEvent(new CustomEvent('diversity.update', { detail: data }))

    // Animate diversity score
    if (data.diversity_score !== undefined) {
      this.animations.animateProgress('diversity-progress', data.diversity_score * 100)
    }
  }

  /**
   * Handle economics update
   */
  private handleEconomicsUpdate(data: Partial<SupplyResponse>) {
    window.dispatchEvent(new CustomEvent('economics.update', { detail: data }))

    // Pulse supply indicator
    this.animations.pulseElement('supply-indicator', {
      color: '#f59e0b',
      pulseCount: 1
    })
  }

  /**
   * Handle treasury update
   */
  private handleTreasuryUpdate(data: Partial<TreasuryResponse> & { new_proposal?: Partial<ProposalSummary> }, priority: string) {
    window.dispatchEvent(new CustomEvent('treasury.update', { detail: data }))

    // Special animation for new proposals
    if (data.new_proposal && priority === 'high') {
      this.animations.slideIn('treasury-proposal-list', 'left', {
        duration: 600
      })
      this.animations.pulseElement('proposal-badge', {
        color: '#10b981',
        pulseCount: 2
      })
    }
  }

  /**
   * Handle balance change event
   */
  private handleBalanceChanged(data: {
    address?: string
    balance?: number
    change_type?: 'transfer_in' | 'transfer_out' | 'lock' | 'unlock'
    amount?: number
  }, priority: string) {
    console.log('Balance changed:', data)
    window.dispatchEvent(new CustomEvent('balance.changed', { detail: data }))

    // Animate balance change notification
    if (priority === 'high') {
      this.animations.pulseElement('balance-indicator', {
        color: '#10b981',
        pulseCount: 2
      })
    }

    // Flash the affected wallet if visible
    if (data.address) {
      this.animations.flashValue(`wallet-${data.address.substring(0, 16)}`, {
        color: data.change_type === 'transfer_in' ? '#10b981' : '#ef4444'
      })
    }
  }

  /**
   * Handle new transaction event
   */
  private handleTransaction(data: Partial<TransactionEntry>, priority: string) {
    console.log('New transaction:', data)
    window.dispatchEvent(new CustomEvent('transaction.new', { detail: data }))

    // Animate transaction notification
    if (priority === 'high') {
      this.animations.pulseElement('transaction-feed', {
        color: '#3b82f6',
        pulseCount: 1
      })
    }
  }

  /**
   * Handle k-core update event
   */
  private handleKCoreUpdate(data: {
    k_value?: number
    finalized_count?: number
    [key: string]: any
  }) {
    window.dispatchEvent(new CustomEvent('kcore.update', { detail: data }))

    // Flash k-core indicator
    if (data.k_value) {
      this.animations.flashValue('kcore-indicator', {
        color: '#3b82f6'
      })
    }
  }

  /**
   * Handle admissibility update event
   */
  private handleAdmissibilityUpdate(data: Partial<AdmissibilityOverviewData> & {
    rates?: { overall?: number }
  }) {
    window.dispatchEvent(new CustomEvent('admissibility.update', { detail: data }))
  }

  /**
   * Handle message rejected event
   */
  private handleMessageRejected(data: {
    message_id?: string
    reason?: string
    [key: string]: any
  }, priority: string) {
    console.warn('Message rejected:', data)
    window.dispatchEvent(new CustomEvent('message.rejected', { detail: data }))

    // Show rejection notification
    if (priority === 'high') {
      this.animations.pulseElement('rejected-messages', {
        color: '#ef4444',
        pulseCount: 2
      })
    }
  }

  /**
   * Handle reputation update event
   */
  private handleReputationUpdate(data: {
    public_key?: string
    address?: string
    score?: number
    score_change?: number
    [key: string]: any
  }) {
    window.dispatchEvent(new CustomEvent('reputation.update', { detail: data }))

    // Animate reputation change
    if (data.public_key) {
      this.animations.flashValue(`reputation-${data.public_key.substring(0, 16)}`, {
        color: data.score_change && data.score_change > 0 ? '#10b981' : '#ef4444'
      })
    }
  }

  /**
   * Handle tips update event
   */
  private handleTipsUpdate(data: {
    tips?: string[]
    count?: number
  }) {
    window.dispatchEvent(new CustomEvent('tips.update', { detail: data }))
  }

  /**
   * Handle deposit events (escrowed, released, slashed)
   */
  private handleDepositEvent(type: string, data: Partial<EscrowTransactionEntry>, priority: string) {
    console.log('Deposit event:', type, data)
    window.dispatchEvent(new CustomEvent(type, { detail: data }))

    // Animate based on deposit type
    const colors = {
      'deposit.escrowed': '#f59e0b',
      'deposit.released': '#10b981',
      'deposit.slashed': '#ef4444'
    }

    if (priority === 'high') {
      this.animations.pulseElement('deposits-indicator', {
        color: colors[type as keyof typeof colors] || '#3b82f6',
        pulseCount: 1
      })
    }
  }

  /**
   * Handle peer events (connected/disconnected)
   */
  private handlePeerEvent(type: string, data: {
    peer_id?: string
    address?: string
    version?: string
    [key: string]: any
  }, priority: string) {
    console.log('Peer event:', type, data)
    window.dispatchEvent(new CustomEvent(type, { detail: data }))

    // Optional: Show notification for peer connections
    if (type === 'peer.connected') {
      this.animations.pulseElement('peer-count-indicator', {
        color: '#10b981',
        pulseCount: 1
      })
    }
  }

  /**
   * Handle sync events (started/progress/completed)
   */
  private handleSyncEvent(type: string, data: Partial<ActiveSync> & {
    progress_percent?: number
    synced_messages?: number
    started_at?: string
  }, priority: string) {
    console.log('Sync event:', type, data)
    window.dispatchEvent(new CustomEvent(type, { detail: data }))

    // Optional: Update sync progress indicator
    if (type === 'sync.progress' && data.progress_percent !== undefined) {
      this.animations.animateProgress('sync-progress', data.progress_percent)
    } else if (type === 'sync.completed') {
      this.animations.pulseElement('sync-status', {
        color: '#10b981',
        pulseCount: 2
      })
    }
  }

  /**
   * Handle node lifecycle events (started/stopped/genesis)
   */
  private handleNodeEvent(type: string, data: {
    node_id?: string
    version?: string
    genesis_hash?: string
    [key: string]: any
  }, priority: string) {
    console.log('Node event:', type, data)
    window.dispatchEvent(new CustomEvent(type, { detail: data }))

    // Show notification for critical node events
    if (type === 'node.stopped' && priority === 'high') {
      this.animations.pulseElement('node-status', {
        color: '#ef4444',
        pulseCount: 3
      })
    } else if (type === 'node.started') {
      this.animations.pulseElement('node-status', {
        color: '#10b981',
        pulseCount: 2
      })
    }
  }

  /**
   * Subscribe to WebSocket channels
   */
  private subscribeToChannels() {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.warn('⚠️ [LiveUpdateManager] Cannot subscribe - WebSocket not open')
      return
    }

    const channels = [
      'messages',
      'finality',
      'metrics',
      'tips',
      'transactions',
      'reputation',
      'consensus',
      'economics',
      'deposits',
      'peers',
      'sync',
      'network'
    ]

    const subscribeMessage = {
      type: 'subscribe',
      channels: channels
    }

    console.log('📡 [LiveUpdateManager] Sending subscription request:', subscribeMessage)
    this.socket.send(JSON.stringify(subscribeMessage))
    console.log('✅ [LiveUpdateManager] Subscribed to channels:', channels)
  }

  /**
   * Request full state sync from server
   */
  private requestStateSync() {
    if (this.pendingSyncRequest || this.syncInProgress) return

    console.log('Requesting state sync')
    this.pendingSyncRequest = true

    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({
        type: 'sync_request',
        current_version: this.stateVersion.value?.version || 0
      }))
    }

    // Clear flag after timeout
    setTimeout(() => {
      this.pendingSyncRequest = false
    }, 5000)
  }

  /**
   * Setup event handlers for application events
   */
  private setupEventHandlers() {
    // Listen for manual refresh requests
    window.addEventListener('request-live-refresh', () => {
      this.requestStateSync()
    })
  }

  /**
   * Setup update subscriptions for various components
   */
  private setupUpdateSubscriptions() {
    // Subscribe to high-priority updates
    const highPriorityEvents = [
      'dag.message.new',
      'consensus.energy.conflict',
      'economics.transaction.new',
      'economics.treasury.proposal'
    ]

    highPriorityEvents.forEach(event => {
      this.liveUpdates.subscribe(event, (data) => {
        console.log(`High priority event: ${event}`, data)
      }, 'high')
    })
  }

  /**
   * Start collecting metrics
   */
  private startMetricsCollection() {
    setInterval(() => {
      this.calculateMetrics()
    }, 5000)
  }

  /**
   * Calculate update metrics
   */
  private calculateMetrics() {
    const now = Date.now()
    const cutoff = now - 60000 // Last minute

    // Filter timestamps to last minute
    this.updateTimestamps = this.updateTimestamps.filter(t => t > cutoff)

    // Calculate update rate
    this.metrics.value.updateRate = this.updateTimestamps.length / 60

    // Calculate average latency
    if (this.latencyMeasurements.length > 0) {
      const sum = this.latencyMeasurements.reduce((a, b) => a + b, 0)
      this.metrics.value.averageLatency = sum / this.latencyMeasurements.length

      // Keep only recent measurements
      if (this.latencyMeasurements.length > 100) {
        this.latencyMeasurements = this.latencyMeasurements.slice(-100)
      }
    }
  }

  /**
   * Handle disconnection
   */
  private handleDisconnection() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      console.log(`Reconnecting in ${this.reconnectDelay}ms (attempt ${this.reconnectAttempts + 1})`)

      setTimeout(() => {
        this.reconnectAttempts++
        this.reconnectDelay = Math.min(this.reconnectDelay * 1.5, 30000)
        this.connect()
      }, this.reconnectDelay)
    } else {
      console.error('Max reconnection attempts reached')
      window.dispatchEvent(new CustomEvent('websocket-failed', {
        detail: { reason: 'max_attempts_reached' }
      }))
    }
  }

  /**
   * Handle connection errors
   */
  private handleConnectionError() {
    this.connectionStatus.value = 'disconnected'
    this.metrics.value.droppedUpdates++
  }

  /**
   * Start heartbeat
   * NOTE: Disabled - server already sends heartbeat messages for keepalive
   */
  private startHeartbeat() {
    // Client-side ping disabled - server sends heartbeat messages
  }

  /**
   * Stop heartbeat
   */
  private stopHeartbeat() {
    // No-op - heartbeat disabled
  }

  /**
   * Get connection status
   */
  getConnectionStatus() {
    return this.connectionStatus.value
  }

  /**
   * Get update metrics
   */
  getMetrics() {
    return this.metrics.value
  }

  /**
   * Get state version
   */
  getStateVersion() {
    return this.stateVersion.value
  }

  /**
   * Get streaming status information
   */
  getStreamingStatus() {
    return {
      connectionStatus: this.connectionStatus.value,
      isConnected: this.socket?.readyState === WebSocket.OPEN,
      metrics: this.metrics.value,
      stateVersion: this.stateVersion.value,
      streamingStore: this.streamingStore
    }
  }

  /**
   * Check if currently streaming (WebSocket connected)
   */
  isStreaming() {
    return this.socket?.readyState === WebSocket.OPEN
  }

  /**
   * Disconnect and cleanup
   */
  disconnect() {
    this.stopHeartbeat()

    if (this.socket) {
      this.socket.close()
      this.socket = null
    }

    this.connectionStatus.value = 'disconnected'
  }
}

/**
 * Get the LiveUpdateManager singleton instance
 * This is safe to call from components/stores after Vue app is mounted
 */
export const useLiveUpdateManager = () => {
  return LiveUpdateManager.getInstance()
}

/**
 * Get WebSocket connection status
 * Use this in stores/components that need to know connection status
 */
export const useWebSocketStatus = () => {
  const manager = LiveUpdateManager.getInstance()
  return {
    connectionStatus: manager.connectionStatus,
    isConnected: computed(() => manager.connectionStatus.value === 'connected')
  }
}